import React, { useState, useCallback } from 'react';
import { User } from 'lucide-react';
import type { User as UserType } from '../../../../../types/userMaintenance';
import './UpdateUserForm.css';

interface UpdateUserFormProps {
  user: UserType;
  onSave: (updatedUser: UserType) => void;
  onCancel: () => void;
}

interface FormData {
  username: string;
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  countryOrigin: string;
  accessLevel: string;
  frontlinerCode: string;
  travelAgency: string;
  group: string;
  isActive: boolean;
}

const UpdateUserForm: React.FC<UpdateUserFormProps> = ({
  user,
  onSave,
  onCancel
}) => {
  const [formData, setFormData] = useState<FormData>({
    username: user.username,
    firstName: user.firstName,
    middleName: user.middleName,
    lastName: user.lastName,
    email: user.email || '',
    countryOrigin: 'Philippines', // Default from image
    accessLevel: user.accessLevel || 'Frontliner',
    frontlinerCode: '', // New field from image
    travelAgency: 'A0203259 - Charles David E Adalem', // From image
    group: user.groupName || 'Frontliner',
    isActive: user.status === 'active'
  });

  const [errors, setErrors] = useState<Partial<FormData>>({});

  const handleInputChange = useCallback((field: keyof FormData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  }, [errors]);

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email format is invalid';
    }
    if (!formData.accessLevel) {
      newErrors.accessLevel = 'Access level is required';
    }
    if (!formData.group) {
      newErrors.group = 'Group is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = useCallback(() => {
    if (!validateForm()) {
      return;
    }

    const updatedUser: UserType = {
      ...user,
      firstName: formData.firstName,
      middleName: formData.middleName,
      lastName: formData.lastName,
      email: formData.email,
      accessLevel: formData.accessLevel,
      groupName: formData.group,
      status: formData.isActive ? 'active' : 'inactive'
    };

    onSave(updatedUser);
  }, [formData, user, onSave, validateForm]);

  const handleSearchTravelAgency = () => {
    // Placeholder for search functionality
    alert('Travel Agency search functionality would be implemented here');
  };

  return (
    <div className="update-user-form">
      {/* Header */}
      <div className="update-user-header">
        <div className="update-user-header-left">
          <div className="update-user-icon">
            <User size={24} />
          </div>
          <div className="update-user-title-section">
            <h1 className="update-user-title">Update User</h1>
            <div className="update-user-subtitle">
              Edit and manage user account details and permissions
            </div>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="update-user-content">
        <div className="update-user-form-grid">
          {/* Left Column */}
          <div className="update-user-form-column">
            {/* Username */}
            <div className="update-user-field">
              <label className="update-user-field-label">Username</label>
              <input
                type="text"
                value={formData.username}
                disabled
                className="update-user-field-input disabled"
              />
            </div>

            {/* First Name */}
            <div className="update-user-field">
              <label className="update-user-field-label">First Name</label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                className={`update-user-field-input ${errors.firstName ? 'error' : ''}`}
              />
              {errors.firstName && (
                <span className="update-user-field-error">{errors.firstName}</span>
              )}
            </div>

            {/* Email Address */}
            <div className="update-user-field">
              <label className="update-user-field-label">Email Address</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={`update-user-field-input ${errors.email ? 'error' : ''}`}
              />
              {errors.email && (
                <span className="update-user-field-error">{errors.email}</span>
              )}
            </div>

            {/* Access Level */}
            <div className="update-user-field">
              <label className="update-user-field-label">Access Level</label>
              <select
                value={formData.accessLevel}
                onChange={(e) => handleInputChange('accessLevel', e.target.value)}
                className={`update-user-field-select ${errors.accessLevel ? 'error' : ''}`}
              >
                <option value="Frontliner">Frontliner</option>
                <option value="Administrator">Administrator</option>
                <option value="Supervisor">Supervisor</option>
                <option value="Developer">Developer</option>
                <option value="Partner">Partner</option>
                <option value="Corporate">Corporate</option>
                <option value="Analyst">Analyst</option>
                <option value="Support">Support</option>
                <option value="User">User</option>
              </select>
              {errors.accessLevel && (
                <span className="update-user-field-error">{errors.accessLevel}</span>
              )}
            </div>

            {/* Travel Agency */}
            <div className="update-user-field">
              <label className="update-user-field-label">Travel Agency</label>
              <div className="update-user-field-with-button">
                <input
                  type="text"
                  value={formData.travelAgency}
                  onChange={(e) => handleInputChange('travelAgency', e.target.value)}
                  className="update-user-field-input"
                />
                <button
                  type="button"
                  onClick={handleSearchTravelAgency}
                  className="update-user-search-button"
                >
                  Search
                </button>
              </div>
            </div>

            {/* Active User */}
            <div className="update-user-field">
              <label className="update-user-field-label">Active User?</label>
              <div className="update-user-radio-group">
                <label className="update-user-radio-option">
                  <input
                    type="radio"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={() => handleInputChange('isActive', true)}
                  />
                  <span className="update-user-radio-text">Yes</span>
                </label>
                <label className="update-user-radio-option">
                  <input
                    type="radio"
                    name="isActive"
                    checked={!formData.isActive}
                    onChange={() => handleInputChange('isActive', false)}
                  />
                  <span className="update-user-radio-text">No</span>
                </label>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="update-user-form-column">
            {/* Last Name */}
            <div className="update-user-field">
              <label className="update-user-field-label">Last Name</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                className={`update-user-field-input ${errors.lastName ? 'error' : ''}`}
              />
              {errors.lastName && (
                <span className="update-user-field-error">{errors.lastName}</span>
              )}
            </div>

            {/* Middle Name */}
            <div className="update-user-field">
              <label className="update-user-field-label">Middle Name</label>
              <input
                type="text"
                value={formData.middleName}
                onChange={(e) => handleInputChange('middleName', e.target.value)}
                className="update-user-field-input"
              />
            </div>

            {/* Country Origin */}
            <div className="update-user-field">
              <label className="update-user-field-label">Country Origin</label>
              <select
                value={formData.countryOrigin}
                onChange={(e) => handleInputChange('countryOrigin', e.target.value)}
                className="update-user-field-select"
              >
                <option value="Philippines">Philippines</option>
                <option value="Singapore">Singapore</option>
                <option value="Malaysia">Malaysia</option>
                <option value="Thailand">Thailand</option>
                <option value="Indonesia">Indonesia</option>
                <option value="Vietnam">Vietnam</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Frontliner Code */}
            <div className="update-user-field">
              <label className="update-user-field-label">Frontliner Code</label>
              <input
                type="text"
                value={formData.frontlinerCode}
                onChange={(e) => handleInputChange('frontlinerCode', e.target.value)}
                className="update-user-field-input"
              />
            </div>

            {/* Group */}
            <div className="update-user-field">
              <label className="update-user-field-label">Group</label>
              <select
                value={formData.group}
                onChange={(e) => handleInputChange('group', e.target.value)}
                className={`update-user-field-select ${errors.group ? 'error' : ''}`}
              >
                <option value="Frontliner">Frontliner</option>
                <option value="Administrator">Administrator</option>
                <option value="Supervisor">Supervisor</option>
                <option value="Developer">Developer</option>
                <option value="Partner">Partner</option>
                <option value="Corporate">Corporate</option>
                <option value="Analyst">Analyst</option>
                <option value="Support">Support</option>
                <option value="User">User</option>
              </select>
              {errors.group && (
                <span className="update-user-field-error">{errors.group}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="update-user-footer">
        <button
          type="button"
          onClick={onCancel}
          className="update-user-btn-back"
        >
          Back to List
        </button>
        <button
          type="button"
          onClick={handleSave}
          className="update-user-btn-save"
        >
          Save Changes
        </button>
      </div>
    </div>
  );
};

export default UpdateUserForm;